import React from 'react';
import { sectionIcons } from './sectionIcons';
import VersionHint from './VersionHint';

// Read-only sibling of SectionCard: same outer chrome (rounded card, icon circle, title,
// description) so the personnel detail page reads as part of the same design system as the
// staff application itself — but always open (a director reviewing a submitted record wants
// everything visible, not a form to fill in) and rendering label/value rows instead of
// QuestionField inputs, since nothing here is editable.
//
// `rows` values arrive pre-rendered (via formatAnswer/renderValue in the caller) rather than a
// raw question/answer pair, which keeps this component purely presentational and lets it serve
// both a form section's own questions and the "answered after submission" rows appended to it,
// with the same code path.
//
// Self-manages its own bottom margin (tw-mb-4) rather than relying on a tw-space-y-4 wrapper the
// way SectionCard's parent does: its own parent, editCounselor.js, sits outside
// tailwind.config.js's content glob and cannot safely carry tw- classes of its own.
//
// `aside` is an optional node pinned to the right of the header row. Only the first card on the
// page is given one, carrying the version this application came from — it belongs at the top of
// the record, not floating above the cards with nothing to sit in.
const StaffFormReadOnlySection = ({ section, rows, aside }) => {
    if (!rows.length) return null;
    const Icon = sectionIcons[section.icon];

    return (
        <section
            aria-labelledby={`heading-${section.sectionKey}`}
            className="tw-mb-4 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-sand-200 tw-bg-white tw-shadow-sm">
            <div className="tw-flex tw-items-center tw-gap-4 tw-px-5 tw-py-4">
                <span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-bg-brand-50 tw-text-brand-700">
                    {Icon ? <Icon className="tw-h-5 tw-w-5" aria-hidden="true" /> : null}
                </span>
                <span className="tw-min-w-0 tw-flex-1">
                    <span id={`heading-${section.sectionKey}`} className="tw-block tw-truncate tw-text-base tw-font-semibold tw-text-gray-900">
                        {section.title}
                    </span>
                    {section.description ? (
                        <span className="tw-mt-0.5 tw-block tw-truncate tw-text-xs tw-text-gray-500">{section.description}</span>
                    ) : null}
                </span>
                {aside ? <span className="tw-shrink-0 tw-text-right">{aside}</span> : null}
            </div>
            <div className="tw-border-t tw-border-sand-200 tw-px-5 tw-py-6">
                <div className="tw-grid tw-grid-cols-1 tw-gap-x-5 tw-gap-y-5 sm:tw-grid-cols-2">
                    {rows.map(row => (
                        <div key={row.key}>
                            {/* Matches QuestionField's own label treatment (tw-text-sm tw-font-semibold
                                tw-text-gray-800) rather than SectionCard's uppercase step-kicker style — that
                                one is for a two-word eyebrow, not a full question label, and reads badly on the
                                raw questionKey fallback labels (a pre-versioning orphan with no resolvable
                                label anywhere) below. */}
                            <p className="tw-text-sm tw-font-semibold tw-text-gray-800">
                                {row.label}
                                {/* inSnapshot: false in the caller — an answer that exists but was not part of
                                    the form this person originally submitted (a director filled it in later, or
                                    it predates versioning). Flagging it here is what replaces the old, now-wrong
                                    "hidden" tag this page used to show.

                                    An icon rather than the "Added after submission" text it used to be: two of
                                    these landing in adjacent columns crowded the labels they were annotating,
                                    and the same hint on the editable form had to become an icon anyway to stop
                                    it breaking that grid's alignment. One treatment on both screens. */}
                                {row.extra ? (
                                    <VersionHint
                                        label="Why is this answer here?"
                                        text="Not part of the form version this application was submitted on. It was answered later, during an edit." />
                                ) : null}
                            </p>
                            {/* break-words: an unrecognized fallback value can be an unbroken string (raw
                                JSON, a stray base64 fragment) long enough to overflow its column otherwise. */}
                            <p className="tw-mt-1 tw-break-words tw-text-sm tw-text-gray-900">{row.value}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default StaffFormReadOnlySection;
